import { useCallback, useEffect, useRef, useState } from "react";
import { getFamilyJob } from "../lib/api";
import type { FamilyRunProgress } from "../lib/types";

const TERMINAL = new Set(["ok", "error", "interrupted", "cancelled", "budget_exceeded"]);
const POLL_MS = 1500;
const MAX_CONSECUTIVE_FAILURES = 20;

export type FamilyJobState = {
  runId: string;
  status: string;
  progress: FamilyRunProgress | null;
  error: string;
};

/**
 * Poll one project-family run until it ends (the lead-generation hook's pattern).
 *
 * `storageKey` remembers the running job for this browser tab, so leaving the
 * page and coming back resumes the progress line instead of losing the run.
 * Each start opens a new polling chain and invalidates the previous one, so a
 * reply that lands after unmount or a restart can neither re-arm polling nor
 * report the finish a second time.
 */
export function useFamilyClassificationJob(
  storageKey: string,
  onFinished?: (state: FamilyJobState) => void,
) {
  const [state, setState] = useState<FamilyJobState | null>(null);
  const timer = useRef<number | null>(null);
  const chain = useRef(0);
  const failures = useRef(0);
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;

  const stop = useCallback(() => {
    chain.current += 1;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const remember = useCallback(
    (runId: string) => {
      try {
        if (runId) sessionStorage.setItem(storageKey, runId);
        else sessionStorage.removeItem(storageKey);
      } catch {
        /* storage is a convenience */
      }
    },
    [storageKey],
  );

  const poll = useCallback(
    async (runId: string, id: number) => {
      let data: Awaited<ReturnType<typeof getFamilyJob>> | null = null;
      let failure: unknown = null;
      try {
        data = await getFamilyJob(runId, { timeoutMs: 30_000 });
      } catch (e) {
        failure = e;
      }
      if (chain.current !== id) return;
      if (data) {
        failures.current = 0;
        const next: FamilyJobState = {
          runId,
          status: String(data.status || ""),
          progress: data.result ?? null,
          error: String(data.error || ""),
        };
        setState(next);
        if (TERMINAL.has(next.status)) {
          chain.current += 1;
          remember("");
          finishedRef.current?.(next);
          return;
        }
      } else {
        failures.current += 1;
        if (failures.current >= MAX_CONSECUTIVE_FAILURES) {
          chain.current += 1;
          remember("");
          const message = failure instanceof Error ? failure.message : "Lost contact with the job";
          setState((prev) => ({ runId, status: "error", progress: prev?.progress ?? null, error: message }));
          return;
        }
      }
      timer.current = window.setTimeout(() => void poll(runId, id), POLL_MS);
    },
    [remember],
  );

  const start = useCallback(
    (runId: string) => {
      stop();
      const id = chain.current;
      failures.current = 0;
      remember(runId);
      setState({ runId, status: "pending", progress: null, error: "" });
      void poll(runId, id);
    },
    [poll, remember, stop],
  );

  useEffect(() => {
    let saved = "";
    try {
      saved = sessionStorage.getItem(storageKey) || "";
    } catch {
      saved = "";
    }
    if (saved) start(saved);
    return stop;
  }, [storageKey, start, stop]);

  const running = Boolean(state && !TERMINAL.has(state.status));
  const clear = useCallback(() => setState(null), []);
  return { state, running, start, clear };
}
